/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { isValidElement, type ReactNode } from 'react';
import {
  toast as baseToast,
  type Id,
  type ToastContent,
  type ToastOptions,
  type UpdateOptions,
} from 'react-toastify';
import './toast.css';
import AlertToastContent from './toast-history/AlertToastContent';
import AlertToastIcon from './toast-history/AlertToastIcon';
import AlertToastMessage from './toast-history/AlertToastMessage';
import {
  clearToastHistory,
  getToastHistorySnapshot,
  type RetainedToastType,
  retainToast,
} from './toast-history/toastHistoryStore';

const ALERT_AUTO_CLOSE_DURATION = 10_000;
const SHORT_ALERT_AUTO_CLOSE_DURATION = 4_000;
export const ALERT_TOAST_CONTAINER_ID = 'alert-container';
let nextAlertToastId = 0;

/**
 * react-toastify v11 renders string content as a bare text node directly inside
 * the toast element — there is no `.Toastify__toast-body` wrapper to style. String
 * alerts render through AlertToastMessage ("title: detail" → bold title + detail line);
 * non-string (JSX) content — used by the notification toasts — passes through untouched.
 *
 * Import `toast` from here instead of from 'react-toastify' everywhere except the
 * `<ToastContainer>` setup in App.tsx.
 */
function wrapContent<TData>(content: ToastContent<TData>): ToastContent<TData> {
  if (typeof content === 'string') {
    return <AlertToastMessage content={content} />;
  }
  return content;
}

// Every alert shares one card; severity shows only through the round status icon.
const alertDefaults: ToastOptions = { containerId: ALERT_TOAST_CONTAINER_ID, icon: AlertToastIcon };
// Success and info are short confirmations: no close button, gone after 4 s.
const shortAlertDefaults: ToastOptions = {
  ...alertDefaults,
  autoClose: SHORT_ALERT_AUTO_CLOSE_DURATION,
  closeButton: false,
};

function getContentSummary(content: ReactNode): string {
  if (typeof content === 'string' || typeof content === 'number' || typeof content === 'bigint') {
    return String(content);
  }
  if (Array.isArray(content)) {
    return (content as ReactNode[]).map(getContentSummary).filter(Boolean).join(' ');
  }
  if (isValidElement<{ children?: ReactNode }>(content)) {
    return getContentSummary(content.props.children);
  }
  return '';
}

type ToastMethod = <TData = unknown>(
  content: ToastContent<TData>,
  options?: ToastOptions<TData>,
) => Id;

function wrapMethod(method: ToastMethod, defaults: ToastOptions = shortAlertDefaults): ToastMethod {
  return (content, options) =>
    method(wrapContent(content), {
      ...(defaults as ToastOptions<never>),
      ...options,
    });
}

function retainMethod(method: ToastMethod, type: RetainedToastType): ToastMethod {
  return <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions<TData>) => {
    nextAlertToastId += 1;
    const id = options?.toastId ?? `pa-alert-${nextAlertToastId}`;
    const retainedContent = typeof content === 'function' ? `${type} notification` : content;
    const summary =
      typeof content === 'function' ? '' : getContentSummary(content).replace(/\s+/g, ' ').trim();
    retainToast({
      id,
      type,
      content: retainedContent,
      summary: summary || `${type === 'error' ? 'Error' : 'Warning'} notification`,
    });
    const renderedContent: ToastContent<TData> =
      typeof content === 'function' ? (
        (props) => <AlertToastContent content={content(props)} id={id} type={type} />
      ) : (
        <AlertToastContent content={content} id={id} type={type} />
      );
    return method(renderedContent, {
      ...(alertDefaults as ToastOptions<never>),
      autoClose: ALERT_AUTO_CLOSE_DURATION,
      ...options,
      toastId: id,
    });
  };
}

const wrappedBase = ((content, options) => {
  if (options?.type === 'error' || options?.type === 'warning') {
    return retainMethod(baseToast as ToastMethod, options.type)(content, options);
  }
  return (baseToast as ToastMethod)(wrapContent(content), {
    ...(shortAlertDefaults as ToastOptions<never>),
    ...options,
  });
}) as typeof baseToast;

export function clearAlertToasts(): void {
  const ids = getToastHistorySnapshot().records.map(({ id }) => id);
  clearToastHistory();
  ids.forEach((id) => {
    baseToast.dismiss({ containerId: ALERT_TOAST_CONTAINER_ID, id });
  });
  baseToast.clearWaitingQueue({ containerId: ALERT_TOAST_CONTAINER_ID });
}

export const toast = Object.assign(wrappedBase, baseToast, {
  success: wrapMethod(baseToast.success),
  info: wrapMethod(baseToast.info),
  error: retainMethod(baseToast.error, 'error'),
  warning: retainMethod(baseToast.warning, 'warning'),
  warn: retainMethod(baseToast.warning, 'warning'),
  loading: wrapMethod(baseToast.loading as ToastMethod, {
    ...alertDefaults,
    closeButton: false,
  }) as typeof baseToast.loading,
  update: <TData = unknown>(id: Id, options: UpdateOptions<TData> = {}) =>
    baseToast.update(
      id,
      options.render !== undefined
        ? {
            containerId: ALERT_TOAST_CONTAINER_ID,
            ...options,
            render: wrapContent(options.render),
          }
        : { containerId: ALERT_TOAST_CONTAINER_ID, ...options },
    ),
});

export type { Id, ToastContent, ToastOptions } from 'react-toastify';
